import { useChartNumberValue, useIsTableShown } from '../../../recoil';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { MultiResolutionChart } from './MultiResolutionChart';
import { useParams } from 'react-router-dom';

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

const SidebySideCharts = ({
  indexes,
  className,
}: {
  indexes: string[];
  className?: string;
}) => {
  return (
    <div className={`flex w-full ${className} `}>
      {indexes.map((id) => (
        <div key={id} className={`${indexes.length == 1 ? 'w-full' : 'w-1/2'}`}>
          <MultiResolutionChart
            market={id.split(':')[0] as any}
            index={+id.split(':')[1]}
            key={id}
          />
        </div>
      ))}
    </div>
  );
};

// full clean
const MarketChart: React.FC<any> = ({ }) => {
  // clean
  const isTableExpanded = useIsTableShown();
  const chartTimes = useChartNumberValue();
  const { market: marketParam } = useParams();
  const [dragging, setDragging] = useState(false);
  const [containerDim, setContainerDim] = useState<{
    height?: number;
    top?: number;
  }>({});

  // clean
  const containerRef = useRef<HTMLDivElement>();
  const onInitialLoad: React.LegacyRef<HTMLDivElement> = useCallback(
    async (ele: HTMLDivElement) => {
      if (!isTableExpanded) return;
      containerRef.current = ele;
      await sleep(1000);
      const d = ele?.getBoundingClientRect();
      if (!d) return;
      const updatedRect = { ...d, height: null };
      // @ts-ignore
      setContainerDim({ height: null, top: d.top });
    },
    [isTableExpanded]
  );

  // clean
  const onMove = (clientY: number) => {
    if (!clientY) return;
    if (!dragging) return;

    setContainerDim((currentChartContainerDim) => {
      if (!currentChartContainerDim?.top) return {};

      let updatedChartContainerDim: Partial<{ height: number; top: number; }> =
        {};
      const updatedHeight = clientY - currentChartContainerDim.top;
      const bound = window.innerHeight - (currentChartContainerDim.top + 50);
      updatedChartContainerDim.height = Math.min(updatedHeight, bound);
      updatedChartContainerDim.top = currentChartContainerDim.top;

      // since doccument.onmouseup doesn't get fired on TV area, we have to
      // take an offset value while scrolling up
      if (
        containerRef.current &&
        containerRef.current?.getBoundingClientRect().height -
        updatedChartContainerDim.height >
        35
      ) {
        onMouseUp();
      }
      return updatedChartContainerDim;
    });
  };

  // clean
  const onMouseMove = (e: MouseEvent) => {
    if (dragging) e.preventDefault();
    onMove(e.clientY);
  };

  const onTouchMove = (e: TouchEvent) => {
    onMove(e.touches[0].clientY);
  };

  const onMouseUp = () => {
    setDragging(false);
  };

  useEffect(() => {
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('touchmove', onTouchMove);
    document.addEventListener('mouseup', onMouseUp);
    document.addEventListener('mouseleave', onMouseUp);
    return () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('touchmove', onTouchMove);
      document.removeEventListener('mouseup', onMouseUp);
    };
  });

  // use this to create market specific prefix for indexes to be used for multi chart
  // eg: BTC-USDT -> BTCUSDT:1, BTCUSDT:2 etc
  const marketPrefix = useMemo(() => marketParam?.replace('-', '') + ':', [marketParam]);

  let chartLayout = (
    <SidebySideCharts indexes={[marketPrefix + 1]} className="h-full" />
  );

  if (chartTimes == 2) {
    chartLayout = (
      <SidebySideCharts
        indexes={[marketPrefix + 1, marketPrefix + 2]}
        className="h-full"
      />
    );
  }

  if (chartTimes == 2.5) {
    chartLayout = (
      <div className="flex-col w-[100%] h-full">
        <SidebySideCharts indexes={[marketPrefix + 1]} className="h-1/2" />
        <SidebySideCharts indexes={[marketPrefix + 2]} className="h-1/2" />
      </div>
    );
  }

  if (chartTimes == 4) {
    chartLayout = (
      <div className="flex-col w-[100%] h-full">
        <SidebySideCharts
          indexes={[marketPrefix + 1, marketPrefix + 2]}
          className="  h-1/2"
        />
        <SidebySideCharts
          indexes={[marketPrefix + 3, marketPrefix + 4]}
          className="  h-1/2"
        />
      </div>
    );
  }

  const onMouseDown = () => {
    setDragging(true);
  };

  // clean
  return (
    <>
      <div
        className={'flex flex-col flex-grow   '}
        style={containerDim?.height ? { height: containerDim.height } : {}}
        ref={onInitialLoad}
      >
        {chartLayout}
      </div>
      {isTableExpanded && (
        <div
          onMouseDown={onMouseDown}
          onTouchStart={onMouseDown}
          onMouseUp={onMouseUp}
          onTouchEnd={onMouseUp}
          // onMouseLeave={onMouseUp}
          className={` w-full   cursor-row-resize h-[5px] hover:bg-blue ${dragging ? ' bg-blue brightness-125' : ''
            }`}
        // onDragStart={onDragStart}
        // onDragEnd={onDragEnd}
        ></div>
      )}
    </>
  );
};

export { MarketChart };
